import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Alert, Button, DialogActions, DialogContent, Typography } from '@mui/material';
import { ApiError } from '../../../../lib/http/ApiError';
import { AppModal } from '../../../../components/ui/AppModal';
import { useNotification } from '../../../notifications/hooks/useNotification';
import { useUpdateUserStatus } from '../../hooks/useUpdateUserStatus';
import { useAssignUserProfile } from '../../hooks/useAssignUserProfile';
import { ProfileSelector } from '../ProfileSelector/ProfileSelector';
import type { User } from '../../userTypes';

interface ReactivateUserDialogProps {
  user: User | null;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Reactivation dialog (HOMCO-1366). PATCH /users/:id/status { is_active: true }.
 *
 * A `users` account that lost its profile (`role === 'users' && profile_id ===
 * null`) must first be given one: the dialog shows a profile selector and runs
 * PUT /users/:id/profile THEN the status PATCH. A 400 UserProfileRequired on the
 * simple path (stale list) flips the dialog into the same profile mode. If the
 * PATCH fails after a successful PUT, the profile stays assigned: we show the
 * error and refresh the row without announcing a reactivation.
 *
 * The profile picker reuses HOMCO-641's ProfileSelector (GET /rights-profiles),
 * which surfaces its own load/forbidden errors; it is mounted only in profile mode,
 * so no profiles are fetched when none is needed (account with a profile, admin/hq).
 */
export function ReactivateUserDialog({ user, onClose, onSuccess }: ReactivateUserDialogProps) {
  const { t } = useTranslation('users');
  const { updateUserStatus, error: statusError } = useUpdateUserStatus();
  const { assignUserProfile, error: assignError } = useAssignUserProfile();
  const { notifySuccess } = useNotification();

  const [submitting, setSubmitting] = useState(false);
  const [profileId, setProfileId] = useState('');
  const [assignThenStatusFailed, setAssignThenStatusFailed] = useState(false);

  // The dialog is mounted only while open (the page unmounts it on close), so it
  // always starts from fresh state — no reset effect needed.

  const needsProfileInitially = user?.role === 'users' && user?.profile_id === null;
  // A 400 on the simple path means the backend wants a profile (stale list).
  const statusRequiresProfile = ApiError.isApiError(statusError) && statusError.statusCode === 400;
  const profileMode = needsProfileInitially || statusRequiresProfile;

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const finishSuccess = () => {
    if (!user) return;
    notifySuccess(t('actions.reactivate.success'));
    onSuccess();
    onClose();
  };

  const handleSimpleConfirm = async () => {
    if (!user) return;
    setSubmitting(true);
    const updated = await updateUserStatus(user.id, true);
    setSubmitting(false);
    if (updated) finishSuccess();
    // else: a 400 flips to profile mode on re-render; any other error shows below.
  };

  const handleProfileConfirm = async () => {
    if (!user || !profileId) return;
    setSubmitting(true);
    setAssignThenStatusFailed(false);
    const assigned = await assignUserProfile(user.id, profileId);
    if (!assigned) {
      setSubmitting(false);
      return; // assignError shown below (403 P5 / 404 / other)
    }
    const updated = await updateUserStatus(user.id, true);
    setSubmitting(false);
    if (updated) {
      finishSuccess();
    } else {
      // Profile is assigned but reactivation failed: keep it, refresh, no success.
      setAssignThenStatusFailed(true);
      onSuccess();
    }
  };

  // ── Error messages ──────────────────────────────────────────────────────────
  let errorMessage: string | null = null;
  if (assignThenStatusFailed) {
    errorMessage = t('actions.reactivate.statusAfterAssignError');
  } else if (profileMode) {
    if (ApiError.isApiError(assignError)) {
      if (assignError.statusCode === 403) errorMessage = t('actions.reactivate.forbidden');
      else if (assignError.statusCode === 404)
        errorMessage = t('actions.reactivate.profileNotFound');
      else errorMessage = t('actions.reactivate.error');
    }
  } else if (statusError) {
    errorMessage = t('actions.reactivate.error');
  }

  return (
    <AppModal
      open={user !== null}
      onClose={handleClose}
      disableClose={submitting}
      maxWidth="xs"
      titleId="reactivate-user-title"
      icon={<CheckCircleOutlineIcon sx={th => ({ fontSize: th.iconSize.sm })} />}
      title={
        profileMode ? t('actions.reactivate.profileTitle') : t('actions.reactivate.confirmTitle')
      }
      closeLabel={t('actions.reactivate.cancel')}
    >
      <DialogContent sx={{ pt: 2 }}>
        {profileMode ? (
          <>
            <Typography sx={{ mb: 2 }}>{t('actions.reactivate.profileMessage')}</Typography>
            <ProfileSelector
              value={profileId || null}
              onChange={setProfileId}
              disabled={submitting}
            />
          </>
        ) : (
          <Typography>
            {t('actions.reactivate.confirmMessage', { username: user?.username ?? '' })}
          </Typography>
        )}
        {errorMessage && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorMessage}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button variant="outlined" onClick={handleClose} disabled={submitting}>
          {t('actions.reactivate.cancel')}
        </Button>
        {profileMode ? (
          <Button
            variant="contained"
            onClick={handleProfileConfirm}
            disabled={submitting || !profileId}
          >
            {t('actions.reactivate.profileConfirm')}
          </Button>
        ) : (
          <Button variant="contained" onClick={handleSimpleConfirm} disabled={submitting}>
            {t('actions.reactivate.confirm')}
          </Button>
        )}
      </DialogActions>
    </AppModal>
  );
}
